import React, { useState } from "react";
import { Category, Transaction, TransactionType } from "../types";
import { formatDay, formatMoney } from "../format";

interface TransactionListProps {
    transactions: Transaction[];
    categoriesById: Record<string, Category>;
    currency: string;
    onDelete: (id: string) => void;
}

type TypeFilter = "all" | TransactionType;

export function TransactionList({ transactions, categoriesById, currency, onDelete }: TransactionListProps) {
    const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");
    const [search, setSearch] = useState("");

    const query = search.trim().toLowerCase();
    const visible = transactions
        .filter(t => typeFilter === "all" || t.type === typeFilter)
        .filter(t => {
            if (!query) {
                return true;
            }
            const category = categoriesById[t.categoryId];
            return t.note.toLowerCase().includes(query) || (category && category.name.toLowerCase().includes(query));
        })
        .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));

    return (
        <section className="fin-card fin-transactions">
            <div className="fin-card-head">
                <h3>Transactions <span className="fin-count">{visible.length}</span></h3>
                <div className="fin-filters">
                    <div className="fin-chips" role="group" aria-label="Filter by type">
                        {(["all", "income", "expense"] as TypeFilter[]).map(f => (
                            <button key={f} className={typeFilter === f ? "active" : ""} onClick={() => setTypeFilter(f)}>
                                {f === "all" ? "All" : f === "income" ? "Income" : "Expenses"}
                            </button>
                        ))}
                    </div>
                    <input type="search" placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)} aria-label="Search transactions" />
                </div>
            </div>
            {visible.length === 0 ? (
                <p className="fin-empty">No transactions match.</p>
            ) : (
                <ul className="fin-list">
                    {visible.map(t => {
                        const category = categoriesById[t.categoryId];
                        return (
                            <li key={t.id}>
                                <span className="fin-list-icon" aria-hidden="true">{category ? category.icon : "•"}</span>
                                <span className="fin-list-main">
                                    <strong>{t.note || (category ? category.name : "Transaction")}</strong>
                                    <small>{category ? category.name : "Uncategorised"} · {formatDay(t.date)}</small>
                                </span>
                                <span className={`fin-list-amount ${t.type}`}>
                                    {t.type === "income" ? "+" : "−"}{formatMoney(t.amount, currency)}
                                </span>
                                <button className="fin-delete" onClick={() => onDelete(t.id)} aria-label={`Delete ${t.note || "transaction"}`}>
                                    ✕
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
        </section>
    );
}
